import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useLocale} from "i18n";
import type {LinkedContractDocument} from "pay/checkoutDocument";
import {settledCheckoutDocument} from "pay/CheckoutDocumentApi";
import {useCheckoutDocument} from "pay/useCheckoutDocument";
import {acceptedAdvertisingTerms, ADVERTISING_TERMS_ENDPOINTS} from "./advertisingTermsDocument";
import {sponsorBuyerDeclaration} from "./sponsorBooking";
import {useKeyedSingleFlight} from "utils/useSingleFlight";
import {useAuthenticatedSession, useIsLoggedIn} from "UserProvider";
import {useLoginModal} from "login/LoginModalProvider";
import {settledCreationAction} from "home/transferCreationAuth";
import {useAuthoritativeSessionWait} from "auth/useAuthoritativeSessionWait";
import {
	SponsorApi,
	type SponsorAssetRole,
	type SponsorAvailabilityDto,
} from "./SponsorApi";
import {
	categoryOf,
	isSubmittable,
	templateAssetRange,
	validateSponsorBooking,
	type SponsorBookingDraft,
	type SponsorField,
	type SponsorFieldErrors,
	type SponsorOptions,
	type BookableTemplateKey,
} from "./sponsorBooking";
import {
	draftSponsorOptions,
	sponsorMediaForWire,
	sponsorOptionsForWire,
	validateSponsorLogoFile,
	validateSponsorPosterFile,
	validateSponsorVideoFile,
	validateSponsorWallpaperImage,
	withSponsorOption,
	type SponsorPickedCreative,
	type SponsorOptionKey,
	type SponsorPickedAsset,
} from "./sponsorOptions";
import {readVideoMetadata} from "./options/videoMetadata";
import {readImageSize} from "./options/imageMetadata";
import {nextRangeSelection, selectionSurviving, type DateRange} from "./sponsorCalendar";
import {compressPickedImage} from "core/images/compressPickedImage";
import {ImageCompressionError} from "core/images/imageCompression";
import {admitPickedFiles, processPickedBatch} from "core/images/pickedImageBatch";
import {BACKDROP_IMAGE_TARGET, LOGO_IMAGE_TARGET} from "core/images/uploadImageTargets";
import {
	bookingErrorKey,
	invalidatesCalendar,
	pickedImageErrorKey,
	PickedImageRefused,
	uploadErrorKey,
	type SponsorErrorKey,
} from "./sponsorErrors";

/**
 * The React adapter for the self-serve booking form (ui#496).
 *
 * A thin adapter by mandate (epic #26): every rule it appears to apply actually lives somewhere that
 * imports no React — what is valid in `sponsorBooking.ts`, what a click does to a date range in
 * `sponsorCalendar.ts`, what a rejection means in `sponsorErrors.ts`. What is left here is genuinely
 * React's job: holding the half-filled form, sequencing the requests, and remembering which fields the
 * sponsor has actually visited yet.
 *
 * It returns error keys rather than sentences; the view owns copy. The selected locale accompanies
 * the recorded buyer declaration so its durable confirmation uses the same language.
 *
 * Since epic #441 a sponsor is an ACCOUNT HOLDER of any tier, so the two WRITE actions — uploading a
 * creative and submitting the booking — route an anonymous visitor to sign-in instead of posting, and
 * hold a sponsor shown signed in until their restored session answers (ui#3117). The page itself stays
 * public: `GET /sponsor/availability` is anonymous, so the calendar and the price are visible to a
 * stranger deciding whether this is worth an account at all.
 */

/**
 * The copy fields the sponsor types, i.e. the draft minus everything derived from other state.
 *
 * Named field by field rather than as an `Omit`, so a field added to the draft has to be placed
 * deliberately — as state the sponsor types, as an upload, or as something derived — instead of
 * silently joining this bag and losing its own reducer.
 */
type SponsorCopyFields = Pick<SponsorBookingDraft,
	"template" | "sponsorName" | "sponsorEmail" | "headline" | "tagline" | "ctaLabel" | "ctaUrl"
	| "consumerBooking" | "earlyStartRequested">;

export type {SponsorField};

/** The three single-asset roles this form uploads beside the treatment's images. */
type SponsorSingleAsset = "logo" | "video" | "poster";

/** One picked creative the batch could not add, named so the sponsor knows WHICH picture failed. */
export interface SponsorUploadFailure {
	/** The picked file's own name. Sponsor-supplied text, not copy — it is echoed, never translated. */
	name: string;
	error: SponsorErrorKey;
}

/**
 * What one multi-file pick did (ui#664), or `null` when the last pick went through untouched.
 *
 * Separate from {@link UseSponsorBooking.requestError} because a batch has no single verdict: four
 * creatives can upload while a fifth is refused and a sixth never had a slot, and collapsing that into
 * one sentence would either hide the successes or hide the failures.
 */
export interface SponsorUploadReport {
	/**
	 * Which REPORTED pick this is, counting from 1. A pick that lands cleanly produces no report and
	 * does not advance this, so it counts problems rather than picks.
	 *
	 * Carried so the view can key on it. Two picks can produce a byte-identical report — the same three
	 * files refused twice over — and without something that changes, React renders identical text into
	 * the live region, no DOM mutation happens, and a screen reader announces NOTHING the second time.
	 * The sponsor would be told their retry failed only if they could see the screen.
	 */
	reportId: number;
	/** Picks dropped for want of a slot — see `admitPickedFiles`. Zero when the whole pick fitted. */
	refused: number;
	/** The files that were admitted but could not be added, in pick order. */
	failures: readonly SponsorUploadFailure[];
}

export interface UseSponsorBooking {
	/** The draft as the domain sees it — assembled from the copy fields, the uploads and the date range. */
	draft: SponsorBookingDraft;
	/** The brand's calendar, rate and currency; `null` until the first load resolves (or if it fails). */
	availability: SponsorAvailabilityDto | null;
	/** The month the grid is showing (`YYYY-MM`), or `null` before availability decides where to open. */
	month: string | null;
	/** The chosen range, or `null` before the first day click. */
	selection: DateRange | null;
	/** Uploaded creative, in the order it will animate. */
	assets: readonly SponsorPickedCreative[];
	/**
	 * How many creatives the CHOSEN treatment can hold — its `templateAssetRange().max`, not the global
	 * `MAX_ASSETS`. The picker caps a pick at this, counts against it, and disables at it, so the
	 * counter beside the tiles cannot promise slots the treatment would then refuse to animate.
	 */
	assetCapacity: number;
	/** Only the errors the sponsor has earned — see {@link UseSponsorBooking.touchField}. */
	errors: SponsorFieldErrors;
	/** What the last multi-file pick did, or `null` when it added everything it was given. */
	uploadReport: SponsorUploadReport | null;
	/** A whole-request failure (`m.sponsor.errors[key]`), as opposed to a per-field one. */
	requestError: SponsorErrorKey | null;
	/** True while the availability calendar is still loading. */
	loading: boolean;
	/**
	 * True while a creative is uploading, or while a pick waits for the restored sign-in to answer
	 * (kitecorp/transferify-ui#3117).
	 */
	uploading: boolean;
	/**
	 * True from the moment Continue is pressed until checkout takes over, including while the press waits
	 * for the restored sign-in to answer (kitecorp/transferify-ui#3117).
	 */
	busy: boolean;
	/** The wallpaper knobs as the sponsor is editing them; `null` for keys they have not set. */
	options: SponsorOptions;
	/** The advertiser's mark, or `null`. Uploaded with `role=LOGO`; optional on every treatment. */
	logo: SponsorPickedAsset | null;
	/** The VIDEO_LOOP creative and its still frame, or `null`. Refused by every other treatment. */
	video: SponsorPickedAsset | null;
	poster: SponsorPickedAsset | null;
	/**
	 * The registered Advertising Terms document the api announced for this brand and language, or `null`
	 * while unknown, unavailable or failed (kitecorp/transferify-api-service#2149). The acceptance
	 * sentence links it, and {@link UseSponsorBooking.submit} echoes its identity back, so the booking
	 * records exactly the bytes the advertiser was shown.
	 */
	advertisingTerms: LinkedContractDocument | null;
	setField: <K extends keyof SponsorCopyFields>(field: K, value: SponsorCopyFields[K]) => void;
	setTemplate: (template: BookableTemplateKey) => void;
	/**
	 * Sets one wallpaper option, or clears it with `undefined`.
	 *
	 * Clearing is a real choice rather than an omission: "no brand colour" means the poster paints its
	 * accent in the tone ink, which is a different poster from one that never had the control.
	 */
	setOption: <K extends SponsorOptionKey>(key: K, value: SponsorOptions[K] | undefined) => void;
	/** Marks a field as visited (on blur), which is what lets its error render. */
	touchField: (field: SponsorField) => void;
	/**
	 * Ticks or unticks the Advertising Terms box (kitecorp/transferify-ui#2602). A tick counts as a
	 * visit, so unticking it again shows the refusal at once rather than on the next submit attempt.
	 */
	setTermsAccepted: (accepted: boolean) => void;
	setMonth: (month: string) => void;
	onDayClick: (date: string) => void;
	/**
	 * Adds every picked creative that fits, in pick order (ui#664).
	 *
	 * Takes a LIST rather than a file: one dialog fills a six-slot treatment, and a single pick is simply
	 * a batch of one. Never rejects — a corrupt or refused picture is reported through
	 * {@link UseSponsorBooking.uploadReport} and the rest of the batch still lands. `opener` is the picker,
	 * where focus returns when a sign-in the pick opens closes (see {@link UseSponsorBooking.submit}).
	 */
	onUploadFiles: (files: readonly File[], opener?: HTMLElement) => Promise<void>;
	onRemoveAsset: (index: number) => void;
	onMoveAsset: (index: number, delta: number) => void;
	/**
	 * Uploads one single-role asset, refusing it in the browser first when the api would refuse it.
	 *
	 * One entry point for all three roles rather than three near-identical ones: they differ only in
	 * which rule judges the picked file and how it is compressed, and both of those are data. A refused
	 * file never reaches the network and reports through {@link UseSponsorBooking.errors} under its own
	 * control's name. `opener` is the picker, as for {@link UseSponsorBooking.onUploadFiles}.
	 */
	onPickAsset: (role: SponsorSingleAsset, file: File, opener?: HTMLElement) => Promise<void>;
	onRemoveSingleAsset: (role: SponsorSingleAsset) => void;
	/**
	 * The booking this form just created, once it has been accepted; `null` until then.
	 *
	 * A submission no longer leaves the app (kitecorp/transferify-api-service#1362), so success has to
	 * be a STATE the page can render rather than a redirect. The booking id is carried because the
	 * confirmation names the booking the advertiser will next be emailed about.
	 */
	submittedBookingId: string | null;
	/**
	 * Validates and books. Takes no money and does not leave the page.
	 *
	 * An anonymous visitor is routed to sign-in instead and nothing is posted (#441); a sponsor shown
	 * signed in whose restored session has not answered yet is held until it does (ui#3117). On success
	 * {@link UseSponsorBooking.submittedBookingId} is set and an operator decides from there; the
	 * advertiser pays later, from their own booking list, once the approval email arrives.
	 *
	 * `opener` is the pressed Continue, where focus returns when a sign-in this press opens closes. A
	 * press held for the restored session is disabled while it waits and loses focus, so the modal can
	 * no longer find it by reading what is focused when it opens.
	 */
	submit: (opener?: HTMLElement) => Promise<void>;
}

const EMPTY_FIELDS: SponsorCopyFields = {
	consumerBooking: false,
	earlyStartRequested: false,
	// The first template in the picker's own order, so the form and the picker agree on the default
	// without either hardcoding a name the other doesn't know.
	template: "CAROUSEL",
	sponsorName: "",
	sponsorEmail: "",
	headline: "",
	tagline: "",
	ctaLabel: "",
	ctaUrl: "",
};

/**
 * Which `POST /sponsor/assets?role=` each single-asset control writes to.
 *
 * A table rather than an uppercase of the control's own name: the two vocabularies are independent —
 * "logo" is what a sponsor sees and `LOGO` is what the api stores — and a derivation would make renaming
 * either one silently rename the other.
 */
const ROLE_ON_THE_WIRE: Record<SponsorSingleAsset, SponsorAssetRole> = {
	logo: "LOGO",
	video: "VIDEO",
	poster: "POSTER",
};

export function useSponsorBooking(): UseSponsorBooking {
	const {locale} = useLocale();
	const isLoggedIn = useIsLoggedIn();
	const [authenticatedSession] = useAuthenticatedSession();
	const {open: openLogin} = useLoginModal();
	// A press by a sponsor shown signed in, before Firebase has answered, waits here (ui#3117). One wait
	// per control, so the pressed one shows busy: Continue through `busy`, the pickers through `uploading`.
	const submitWait = useAuthoritativeSessionWait(authenticatedSession);
	const uploadWait = useAuthoritativeSessionWait(authenticatedSession);
	const [fields, setFields] = useState<SponsorCopyFields>(EMPTY_FIELDS);
	const [assets, setAssets] = useState<readonly SponsorPickedCreative[]>([]);
	const [submittedBookingId, setSubmittedBookingId] = useState<string | null>(null);
	// A new booking starts from black words on a white background (kitecorp/transferify-ui#2416).
	const [options, setOptions] = useState<SponsorOptions>(() => draftSponsorOptions(null));
	// One record rather than three `useState`s: every rule about them is per-ROLE data (which validator,
	// which compression target, which wire role), so the state is keyed the same way.
	const [singles, setSingles] = useState<Record<SponsorSingleAsset, SponsorPickedAsset | null>>(
		{logo: null, video: null, poster: null});
	/*
	 * A refusal that belongs to a PICK rather than to the draft.
	 *
	 * Separate from the validator's own errors because the two become visible at different moments: a
	 * validator message waits until the sponsor has visited the field, while "that file is not a PNG" is
	 * the answer to something they just did and has to appear immediately.
	 */
	const [pickErrors, setPickErrors] =
		useState<Partial<Record<SponsorSingleAsset, SponsorErrorKey>>>({});

	/**
	 * Retires one control's pick refusal by DELETING the key rather than setting it to `undefined`.
	 *
	 * A present key holding `undefined` survives the object spread that overlays these onto the draft's
	 * own errors, and would blank the message underneath it — so a logo the browser refused, then
	 * removed, would leave the poster's "please add one" invisible.
	 */
	const clearPickError = useCallback((role: SponsorSingleAsset) => {
		setPickErrors(current => {
			if (current[role] === undefined) {
				return current;
			}
			const next = {...current};
			delete next[role];
			return next;
		});
	}, []);
	const [selection, setSelection] = useState<DateRange | null>(null);
	// Unticked on every fresh form: the acceptance is the advertiser's act, never a remembered default.
	const [termsAccepted, setTermsAcceptedState] = useState(false);
	const [availability, setAvailability] = useState<SponsorAvailabilityDto | null>(null);
	const [month, setMonth] = useState<string | null>(null);
	const [touched, setTouched] = useState<ReadonlySet<SponsorField>>(new Set());
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const [requestError, setRequestError] = useState<SponsorErrorKey | null>(null);
	const [uploadReport, setUploadReport] = useState<SponsorUploadReport | null>(null);
	const [loading, setLoading] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [busy, setBusy] = useState(false);
	/*
	 * The checkout's own document selection, pointed at the Advertising Terms
	 * (kitecorp/transferify-api-service#2149): one lookup per brand, language and account, a link the
	 * form renders from it, and a reader that refuses to answer for a selection that moved since. The
	 * lookup is not awaited by disabling the submit control — that control is kitecorp/transferify-ui#2938's
	 * — but at the one place the press reads it, in `submit` below, bounded by the lookup's own timeout.
	 */
	const contract = useCheckoutDocument(true, ADVERTISING_TERMS_ENDPOINTS);
	// Stable by design, unlike the object around it, which is new on every render: `submit` depends on this.
	const latestTermsRead = contract.latestRead;

	// Load the calendar once. The opening month comes from the WINDOW the api published, never from
	// today's date: this page is pre-rendered, so a month derived from the clock would be baked into the
	// static HTML at build time and mismatch the client's first render in any later month (ui#74).
	useEffect(() => {
		let active = true;
		(async () => {
			try {
				const loaded = await SponsorApi.getAvailability();
				if (!active) {
					return;
				}
				setAvailability(loaded);
				setMonth(loaded.from.slice(0, 7));
			} catch (e) {
				if (active) {
					setRequestError(bookingErrorKey(e));
				}
			} finally {
				if (active) {
					setLoading(false);
				}
			}
		})();
		return () => {
			active = false;
		};
	}, []);

	// ONE source of truth per value: the uploads own the asset ids and the range owns the dates, so the
	// draft is assembled from them rather than copied into a second piece of state that can fall behind.
	const draft = useMemo<SponsorBookingDraft>(() => ({
		...fields,
		assetIds: assets.map(asset => asset.assetId),
		// The sizes that were READ, not one per picture: an unmeasured file contributes none, and the
		// submit-time shape rule only needs to know whether any picture is too tall for a wallpaper.
		assetSizes: assets.flatMap(asset => asset.size === null ? [] : [asset.size]),
		startsOn: selection?.startsOn ?? "",
		endsOn: selection?.endsOn ?? "",
		options,
		logoAssetId: singles.logo?.assetId ?? null,
		videoAssetId: singles.video?.assetId ?? null,
		posterAssetId: singles.poster?.assetId ?? null,
		termsAccepted,
	}), [fields, assets, selection, options, singles, termsAccepted]);
	/*
	 * The draft as it stands NOW, for a Continue that has awaited something since it was pressed. A press
	 * held for the restored session (ui#3117) resumes from the closure of the render it was pressed in,
	 * while the form stays editable: the sponsor may have unticked the Advertising Terms or withdrawn an
	 * early start meanwhile, and neither may be sent on their behalf.
	 */
	const draftNow = useRef(draft);
	draftNow.current = draft;

	// The rule for what is wrong is the domain's; the rule for what is SHOWN is this hook's, because it
	// is the only layer that knows what the sponsor has touched. Nobody wants "please write a headline"
	// shouted at them before they have reached the headline.
	const errors = useMemo<SponsorFieldErrors>(() => {
		const all = validateSponsorBooking(draft);
		if (submitAttempted) {
			return all;
		}
		const visible: SponsorFieldErrors = {};
		for (const field of Object.keys(all) as SponsorField[]) {
			if (touched.has(field)) {
				visible[field] = all[field];
			}
		}
		return visible;
	}, [draft, touched, submitAttempted]);

	/*
	 * A file the browser refused OVERWRITES the draft's own message for that control, and the order is
	 * the point: a rejected logo leaves the draft with no logo at all, so the validator would answer
	 * "please add one" over the top of "that one was a JPEG" and hide the only thing the sponsor can act
	 * on.
	 */
	const visibleErrors = useMemo<SponsorFieldErrors>(
		() => ({...errors, ...pickErrors}), [errors, pickErrors]);

	const touchField = useCallback((field: SponsorField) => {
		setTouched(current => (current.has(field) ? current : new Set(current).add(field)));
	}, []);

	// A stale "those dates were taken" banner over a form the sponsor has since changed is worse than no
	// banner: it describes a request that no longer exists.
	const clearRequestError = useCallback(() => setRequestError(null), []);

	const setTermsAccepted = useCallback((accepted: boolean) => {
		setTermsAcceptedState(accepted);
		touchField("termsAccepted");
	}, [touchField]);

	const setField = useCallback(<K extends keyof SponsorCopyFields>(field: K, value: SponsorCopyFields[K]) => {
		setFields(current => ({...current, [field]: value}));
		clearRequestError();
	}, [clearRequestError]);

	const setTemplate = useCallback((template: BookableTemplateKey) => {
		setFields(current => ({...current, template}));
		// The arity a template demands changes with the template, so a picker change can make an
		// already-touched asset list newly wrong. Showing that immediately is the point.
		clearRequestError();
		// The report counts slots against the treatment that was chosen when the pick happened, so a new
		// treatment retires it: "2 images weren't added, this holds 1" is a lie once this holds six.
		setUploadReport(null);
	}, [clearRequestError]);

	/**
	 * Re-reads the calendar after the server has refused the sponsor's range.
	 *
	 * The refusal is the moment we learn the copy loaded at mount is stale, and it was previously the
	 * moment nothing happened: the grid kept painting the taken days as free while the banner told the
	 * sponsor to pick another period, and pressing Continue again earned the identical 409 (api#502).
	 *
	 * A refresh that fails is deliberately silent rather than reported. The sponsor already has the
	 * accurate, specific message — their range was taken — and overwriting it with "something went wrong"
	 * would describe a request they never made and hide the one that actually failed.
	 */
	const refreshCalendar = useCallback(async () => {
		try {
			const loaded = await SponsorApi.getAvailability();
			setAvailability(loaded);
			setSelection(current => selectionSurviving(current, loaded));
		} catch {
			// Intentionally kept: see above. The clash is still the accurate news.
		}
	}, []);

	const onDayClick = useCallback((date: string) => {
		if (availability === null) {
			return;
		}
		setSelection(current => nextRangeSelection(current, date, availability));
		touchField("startsOn");
		clearRequestError();
	}, [availability, touchField, clearRequestError]);

	/**
	 * The admission check both WRITE actions run before touching the network, returning true when the
	 * caller must STOP — the visitor is not signed in and did not become so, or the page went away while
	 * the press waited.
	 *
	 * `settledCreationAction` is the app's existing shared rule (`home/transferCreationAuth.ts`, also used
	 * by the public reminder-upload flow) rather than a fresh `isLoggedIn` read: waiting for the session to
	 * reach its AUTHENTICATED terminal state is what stops a request going out with the displayed identity
	 * but no token yet, which the api would answer 401. It is a courtesy gate — the server is still the
	 * only thing that decides — but it turns "your booking failed" into "sign in". A sponsor the page
	 * shows signed in whose restored session has not answered yet is held on `awaitSession` rather than
	 * asked to sign in (owner decision 2026-09-29, ui#3117).
	 *
	 * A completed sign-in RESUMES the action that hit this gate (ui#1242) rather than discarding it: a
	 * sponsor who filled the form and pressed Continue should not have to find and press it again. The
	 * modal's promise is the re-checked gate — it answers `true` only once the app session reaches the
	 * very terminal state this rule tests — and a dismissal answers `false`, which resumes nothing.
	 */
	const requireSignIn = useCallback(async (
		awaitSession: () => Promise<void>, opener: HTMLElement | undefined,
	): Promise<boolean> => {
		switch (await settledCreationAction(isLoggedIn, authenticatedSession, awaitSession)) {
			case "create":
				return false;
			case "waitCancelled":
				return true;
			case "requestSignIn":
				return !(await openLogin(opener));
		}
	}, [isLoggedIn, authenticatedSession, openLogin]);

	// Keys are per ACTION (as in `account/hooks/useAccountBrand.ts`) so an in-flight upload never blocks
	// the submit, and vice versa. The gate is synchronous, which is what makes it win the race a
	// `disabled` prop loses: `busy` only reaches the button one render after the click.
	const flight = useKeyedSingleFlight();

	// A ref, not state: nothing renders this number, it only has to DIFFER between two picks so the
	// alert remounts and is announced again. Held across renders for the same reason the single-flight
	// gate is — a counter rebuilt each render would hand every pick the same value.
	const reports = useRef(0);
	const nextReportId = () => {
		reports.current += 1;
		return reports.current;
	};

	// The treatment's own ceiling rather than the global MAX_ASSETS: a pick can only usefully fill the
	// slots the CHOSEN treatment animates, and admitting a third creative under a one-image Hero would
	// hand the sponsor a draft `validateSponsorBooking` immediately calls "too many".
	const assetCapacity = templateAssetRange(fields.template).max;

	/*
	 * Whether a picked picture has to be LANDSCAPE (kitecorp/transferify-ui#2143).
	 *
	 * A wallpaper's picture is the whole recipient page, so every window crops it and a phone keeps
	 * about a third of a 16:9 frame's width - the bound exists so there is something left to keep. A
	 * BACKDROP composes inside a frame beside the download card, crops nothing full-bleed, and takes a
	 * tall photograph happily; refusing one there would withdraw a creative the product accepts.
	 */
	const cropsFullBleed = categoryOf(fields.template) === "WALLPAPER";

	const onUploadFiles = useCallback((
		files: readonly File[], opener?: HTMLElement,
	): Promise<void> => flight.run("upload", async () => {
		// Before `setUploading`, so a visitor sent to sign-in never sees the control claim it is working.
		// A press held for the restored session does show working, through `uploadWait`: it was pressed.
		if (await requireSignIn(uploadWait.wait, opener)) {
			return;
		}
		// The cap is applied ONCE, against the set as it stands, rather than re-checked per file: the
		// admitted prefix is what the sponsor sees uploading, and a mid-batch recount would make the
		// number of images that survive depend on how fast each one compressed.
		const {admitted, refused} = admitPickedFiles(files, assets.length, assetCapacity);
		if (admitted.length === 0 && refused === 0) {
			return;
		}
		// Retired before the work starts, not merely replaced after it. A batch is deliberately
		// sequential, so leaving the last pick's failures up would sit a stale "bad.svg: unsupported"
		// over the panel for as long as six compressions and six uploads take.
		setUploadReport(null);
		setUploading(true);
		try {
			const report = await processPickedBatch(admitted, async file => {
				/*
				 * MEASURED FOR EVERY CATEGORY, refused only where the crop is full bleed.
				 *
				 * The refusal is a wallpaper's: a backdrop composes inside a frame beside the download
				 * card and takes a tall photograph happily. The MEASUREMENT is not, and that is the
				 * review's finding: `setTemplate` keeps the pictures across a category change, so a file
				 * picked under a carousel can become a wallpaper's whole page later, and a size read
				 * only for wallpapers would leave `validateSponsorMedia` with nothing to judge it by.
				 *
				 * Judged before anything is decoded, compressed or uploaded, so a file the wallpaper
				 * stage has no answer for costs the advertiser nothing but the pick. It is reported per
				 * file through the batch's own failure path, beside the picture's name, because a
				 * six-file pick can legitimately contain one tall photograph.
				 */
				const size = await readImageSize(file);
				if (cropsFullBleed) {
					const refusal = validateSponsorWallpaperImage(size);
					if (refusal !== null) {
						throw new PickedImageRefused(refusal);
					}
				}
				// Shrunk to what `/sponsor/assets` stores rather than refused for exceeding it (ui#601). A
				// creative already inside the ceiling is uploaded byte-for-byte.
				const ready = await compressPickedImage(file, BACKDROP_IMAGE_TARGET);
				// The size is the SOURCE's, kept beside the uploaded id: compression preserves the aspect
				// ratio, and it is the ratio the crop rule reads.
				return {...await SponsorApi.uploadAsset(ready), size};
			});
			// One append for the whole batch rather than one per file. The order is already the pick's
			// either way — `processPickedBatch` runs them in sequence — so what this buys is that the
			// batch reaches the tile row as a single change, beside the report that explains it.
			if (report.succeeded.length > 0) {
				setAssets(current => [...current, ...report.succeeded]);
				clearRequestError();
			}
			// One place decides what a failed file MEANS - a client refusal, a picture the browser could
			// not process, or the api's verdict read in this picker's vocabulary (`sponsorErrors.ts`).
			const failures = report.failures.map(failure => ({
				name: failure.picked.name,
				error: pickedImageErrorKey(failure.error),
			}));
			// `null` only when the pick was entirely clean, so the panel says nothing after a pick that
			// simply worked — and says everything after one that did not.
			setUploadReport(refused === 0 && failures.length === 0
				? null
				: {reportId: nextReportId(), refused, failures});
		} finally {
			setUploading(false);
			// Touched either way: having tried to add an image, the sponsor is entitled to hear that the
			// chosen template wants more of them.
			touchField("assetIds");
		}
	}), [flight, assets.length, assetCapacity, cropsFullBleed, touchField, clearRequestError,
		requireSignIn, uploadWait.wait]);

	const setOption = useCallback(<K extends SponsorOptionKey>(
		key: K, value: SponsorOptions[K] | undefined,
	) => {
		setOptions(current => withSponsorOption(current, key, value));
		// Every option control is visible the moment a wallpaper is chosen, so touching one is the only
		// signal that the sponsor has reached it — the same rule the copy fields use on blur.
		touchField(key);
		clearRequestError();
	}, [touchField, clearRequestError]);

	/**
	 * How each single-role asset is judged and prepared, as DATA rather than as three code paths.
	 *
	 * The video has no compression entry, and that absence is deliberate: re-encoding video in a browser
	 * is not something this app does, so an oversized file is refused with a message rather than silently
	 * turned into a worse one.
	 */
	const onPickAsset = useCallback((role: SponsorSingleAsset, file: File, opener?: HTMLElement): Promise<void> =>
		flight.run(`pick-${role}`, async () => {
			if (await requireSignIn(uploadWait.wait, opener)) {
				return;
			}
			clearPickError(role);
			const refusal = role === "video"
				? validateSponsorVideoFile(await readVideoMetadata(file))
				: (role === "logo" ? validateSponsorLogoFile(file) : validateSponsorPosterFile(file));
			if (refusal !== null) {
				setPickErrors(current => ({...current, [role]: refusal}));
				touchField(role);
				return;
			}
			setUploading(true);
			try {
				// The video is uploaded as it was picked; the two images are shrunk to what the asset
				// store keeps, exactly as a creative is.
				const ready = role === "video"
					? file
					: await compressPickedImage(file, role === "logo" ? LOGO_IMAGE_TARGET : BACKDROP_IMAGE_TARGET);
				const uploaded = await SponsorApi.uploadAsset(ready, ROLE_ON_THE_WIRE[role]);
				// The name comes from the file the advertiser PICKED, not from the compressed copy: the
				// two can differ, and the one they recognise is theirs.
				setSingles(current => ({...current, [role]: {...uploaded, filename: file.name}}));
				clearRequestError();
			} catch (e) {
				setPickErrors(current => ({
					...current,
					[role]: e instanceof ImageCompressionError
						? ("uploadUnprocessable" as const)
						// The role, so the api's own prose is read with the right vocabulary and shown
						// against the control this file was picked in (`sponsorErrors.ts` says why).
						: uploadErrorKey(e, role),
				}));
			} finally {
				setUploading(false);
				touchField(role);
			}
		}), [flight, requireSignIn, uploadWait.wait, touchField, clearRequestError, clearPickError]);

	const onRemoveSingleAsset = useCallback((role: SponsorSingleAsset) => {
		setSingles(current => ({...current, [role]: null}));
		clearPickError(role);
		touchField(role);
	}, [touchField, clearPickError]);

	const onRemoveAsset = useCallback((index: number) => {
		setAssets(current => current.filter((_, at) => at !== index));
		touchField("assetIds");
		// Only the OVERFLOW half is retired. Freeing a slot is precisely the remedy "2 images didn't fit"
		// asked for, so leaving that up would tell the sponsor there is no room in the same breath as
		// making room. A per-file failure is a different statement — "bad.svg was not a PNG" stays true
		// however many tiles are removed — and dropping it here would erase the only record of which
		// picture the sponsor still has to replace.
		setUploadReport(current => {
			if (current === null || current.refused === 0) {
				return current;
			}
			return current.failures.length === 0
				? null
				: {...current, refused: 0};
		});
	}, [touchField]);

	// A plain bounds-checked swap: the ORDER is the sponsor's editorial choice, not a rule, so there is
	// nothing here for a domain module to own.
	const onMoveAsset = useCallback((index: number, delta: number) => {
		setAssets(current => {
			const target = index + delta;
			if (index < 0 || index >= current.length || target < 0 || target >= current.length) {
				return current;
			}
			const reordered = [...current];
			[reordered[index], reordered[target]] = [reordered[target], reordered[index]];
			return reordered;
		});
	}, []);

	const submit = useCallback((opener?: HTMLElement): Promise<void> => flight.run("submit", async () => {
		// Admission first, ahead of validation: a booking cannot be sent at all without an account, so
		// making a signed-out visitor fix seven field errors before telling them that would be a bait.
		// Nothing is marked as attempted either — the form is left exactly as they left it, and the press
		// after signing in is the one that surfaces what is missing.
		if (await requireSignIn(submitWait.wait, opener)) {
			return;
		}
		// Read once, AFTER the admission: the wait and a sign-in both leave the form live under the press.
		const latest = draftNow.current;
		// Every message becomes visible from here on, so one press surfaces everything still missing
		// rather than making the sponsor discover the next problem on each retry.
		setSubmitAttempted(true);
		if (!isSubmittable(latest)) {
			return;
		}
		setBusy(true);
		try {
			/*
			 * The document the acceptance sentence linked, read through the LATEST reader: the press may
			 * have crossed the sign-in above, which re-begins the lookup for the account that will book, and
			 * a lookup still in flight is awaited here rather than answered with nothing (ui#2566's rule, at
			 * the one boundary this form posts from). A reader whose selection moved since the link rendered
			 * throws, and that reaches the advertiser as a failed submission rather than a booking recorded
			 * under a document they were not shown.
			 */
			const termsRead = await settledCheckoutDocument(latestTermsRead());
			const accepted = acceptedAdvertisingTerms(termsRead);
			// The options as they actually TRAVEL: pruned to the keys the chosen treatment takes and
			// normalized, so a wallpaper's leftover settings can never reach a backdrop booking. Spread
			// rather than assigned, because the request schema documents no null for this field and an
			// absent key is what "no options" is on that wire (see `SponsorApi.SponsorBookingRequestBody`).
			const wireOptions = sponsorOptionsForWire(latest.template, latest.options);
			// The creative pruned the same way, so a video uploaded before the picker moved is kept in
			// the draft and left out of the request rather than earning a refusal for a control the page
			// is no longer showing.
			const wireMedia = sponsorMediaForWire(latest.template, {
				assetIds: latest.assetIds,
				assetSizes: latest.assetSizes,
				logoAssetId: latest.logoAssetId,
				videoAssetId: latest.videoAssetId,
				posterAssetId: latest.posterAssetId,
			});
			const created = await SponsorApi.createBooking({
				buyerDeclaration: sponsorBuyerDeclaration(latest, locale),
				// Spread rather than assigned: absent means "no document was announced", and the api records
				// none for such a booking rather than today's version (see `advertisingTermsDocument.ts`).
				...accepted,
				template: latest.template,
				...(wireOptions === null ? {} : {options: wireOptions}),
				logoAssetId: wireMedia.logoAssetId,
				// The pruned pair, so a picture booking sends two nulls and a video booking sends its two
				// ids — never the leftovers of a treatment the advertiser moved away from.
				videoAssetId: wireMedia.videoAssetId,
				posterAssetId: wireMedia.posterAssetId,
				sponsorName: latest.sponsorName,
				sponsorEmail: latest.sponsorEmail,
				headline: latest.headline,
				// The wire shape says a missing tagline is `null`, and the renderer reserves no space for
				// one — so an untouched (or whitespace) field must not travel as an empty string.
				tagline: latest.tagline.trim() === "" ? null : latest.tagline,
				ctaLabel: latest.ctaLabel,
				ctaUrl: latest.ctaUrl,
				assetIds: [...wireMedia.assetIds],
				startsOn: latest.startsOn,
				endsOn: latest.endsOn,
			});
			/*
			 * Accepted, and nothing has been charged (kitecorp/transferify-api-service#1362). The page
			 * swaps to its confirmation from here; `created.checkoutUrl` is null on this contract and is
			 * deliberately not read. The advertiser pays later, from their booking list.
			 *
			 * `busy` is deliberately NOT cleared: the form is about to be replaced by the confirmation,
			 * and re-enabling the button under it invites a second identical booking, which now costs an
			 * operator a review rather than costing the advertiser a card charge.
			 */
			setSubmittedBookingId(created.bookingId);
		} catch (e) {
			const failure = bookingErrorKey(e);
			setRequestError(failure);
			// Cleared BEFORE the refresh, never after: axios carries no timeout here, so awaiting the
			// re-read first would leave a hung calendar request holding both `busy` and the single-flight
			// key forever — Continue disabled under a "pick another period" banner, with no way out but a
			// reload.
			setBusy(false);
			// A refusal that says our calendar is out of date is the one that re-reads it — the grid then
			// shows what the server actually holds, and a selection it no longer allows is dropped
			// instead of sitting there ready to earn the same refusal.
			if (invalidatesCalendar(failure)) {
				await refreshCalendar();
			}
		}
	}), [flight, locale, requireSignIn, submitWait.wait, refreshCalendar, latestTermsRead]);

	return {
		draft,
		availability,
		month,
		selection,
		assets,
		assetCapacity,
		options,
		logo: singles.logo,
		video: singles.video,
		poster: singles.poster,
		advertisingTerms: contract.document,
		errors: visibleErrors,
		uploadReport,
		requestError,
		loading,
		uploading: uploading || uploadWait.waiting,
		busy: busy || submitWait.waiting,
		setField,
		setTemplate,
		setOption,
		touchField,
		setTermsAccepted,
		setMonth,
		onDayClick,
		onUploadFiles,
		onRemoveAsset,
		onMoveAsset,
		onPickAsset,
		onRemoveSingleAsset,
		submittedBookingId,
		submit,
	};
}
